import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PATH_DASHBOARD } from "../../routes/paths";
import axiosInstance from "../../utils/axios";

const initialState = {
  fetchLoading: false,
  isLoading: false,
  products: [],
  singleProduct: null,
};

export const createProduct = createAsyncThunk(
  "createProduct/product",
  async ({ data, enqueueSnackbar, navigate }, thunkApi) => {
    try {
      const response = await axiosInstance.post("products", data);
      enqueueSnackbar("Product is added successfully");
      navigate(PATH_DASHBOARD.products.list);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchProducts = createAsyncThunk(
  "fetchProducts/product",
  async ({ enqueueSnackbar, limit = 1000, page = 0 }, thunkApi) => {
    try {
      const response = await axiosInstance.get("products", {
        params: {
          limit,
          page: page + 1,
        },
      });

      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchSingleProduct = createAsyncThunk(
  "fetchSingleProduct/product",
  async ({ enqueueSnackbar, slug }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`products/${slug}`,{
        params:{
          with:"variations.type"
        }
      });

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const duplicateProduct = createAsyncThunk(
  "duplicateProduct/product",
  async ({ enqueueSnackbar, slug, navigate }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`products/${slug}`);

      const data = {
        name: `${response.data.data.name} duplicate`,
        description: response.data.data.description,
        category_id: response.data.data.category_id,
        school_id: response.data.data.school_id,
        variations: response.data.data.variations,
        sku: `${response.data.data.sku}D`,
      };

      await thunkApi.dispatch(
        createProduct({ data, enqueueSnackbar, navigate })
      );

      window.location.reload();

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateProduct = createAsyncThunk(
  "updateProduct/product",
  async ({ slug, data, enqueueSnackbar, setOpenEdit }, thunkApi) => {
    try {
      const response = await axiosInstance.post(`products/${slug}`, data);
      return {
        data: response.data.data,
        enqueueSnackbar,
        setOpenEdit,
        slug,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const deleteProduct = createAsyncThunk(
  "deleteProduct/product",
  async ({ id, enqueueSnackbar, limit, page }, thunkApi) => {
    try {
      await axiosInstance.delete(`products/${id}`);
      thunkApi.dispatch(fetchProducts({ limit, page }));

      return { id, enqueueSnackbar };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

// TODO: delte product gallery

export const deleteSingleGallery = createAsyncThunk(
  "deleteSingleGallery/product",
  async (
    {
      id,
      slug,
      img_name,
      enqueueSnackbar,
      setGalleries,
      product_id,
      galleries,
    },
    thunkApi
  ) => {
    try {
      await axiosInstance.patch(`delete-image/${slug}/${img_name}`);
      return {
        setGalleries,
        img_name,
        id,
        enqueueSnackbar,
        product_id,
        galleries,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

const productSlice = createSlice({
  name: "product",
  initialState,
  extraReducers: (builder) => {
    // TODO: create product
    builder.addCase(createProduct.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(createProduct.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(createProduct.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: fetch product
    builder.addCase(fetchProducts.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchProducts.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.products = action.payload;
    });

    builder.addCase(fetchProducts.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: fetch single product
    builder.addCase(fetchSingleProduct.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchSingleProduct.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.singleProduct = action.payload;
    });

    builder.addCase(fetchSingleProduct.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: update product
    builder.addCase(updateProduct.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(updateProduct.fulfilled, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar("Product is updated successfully");
      // state.products.data = state.products.data.map((product) =>
      //   product.slug === action.payload.slug ? action.payload.data : product,
      // );
      // action.payload.setOpenEdit(false);
    });

    builder.addCase(updateProduct.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: delete product
    builder.addCase(deleteProduct.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(deleteProduct.fulfilled, (state, action) => {
      state.isLoading = false;
      state.products.data = state.products.data.filter(
        (product) => product.id !== action.payload.id
      );
      action.payload.enqueueSnackbar("Product is deleted successfully");
    });

    builder.addCase(deleteProduct.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: delete product gallery
    builder.addCase(deleteSingleGallery.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(deleteSingleGallery.fulfilled, (state, action) => {
      state.isLoading = false;
      action.payload.setGalleries((prev) =>
        prev.filter((f, index) => {
          return f !== action.payload.id;
        })
      );

      state.products.data = state.products.data.map((product) =>
        product.id === action.payload.product_id
          ? {
              ...product,
              gallery: action.payload.galleries.filter(
                (f) => f !== action.payload.id
              ),
            }
          : product
      );

      action.payload.enqueueSnackbar(`Gallery image is deleted successfully`);
    });

    builder.addCase(deleteSingleGallery.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================
  },
});

export default productSlice.reducer;
